import { useAction, useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'

import { Result } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { isUserHypedAtom } from '@root/entities/auth/atoms'
import { UserAvatar } from '@root/entities/auth/ui'
import { profileAtom } from '@root/entities/profile'
import { useTranslation } from '@root/shared/localization'
import { AvatarPicker, Input } from '@root/shared/ui-kit'

import { reatomEditProfileForm } from './atoms/reatom-edit-profile-form'

import type { StrictTransFn } from '@root/shared/localization'

export type EditProfileFormProps = {
  onDone?: () => void
}

type EditProfileFormValues = {
  readonly avatar: File | undefined | typeof AVATAR_DELETED
  readonly firstName: string | undefined
  readonly lastName: string | undefined
}

const AVATAR_EXTENSIONS = ['jpeg', 'png', 'webp', 'gif']
const AVATAR_FILESIZE_Mb = 5
const AVATAR_FILESIZE_b = AVATAR_FILESIZE_Mb * 1024 * 1024

const AVATAR_DELETED = Symbol('AVATAR_DELETED')

const validateFileSize =
  (size: number, t: StrictTransFn) =>
  (file?: File | undefined | typeof AVATAR_DELETED) => {
    if (!file || file === AVATAR_DELETED) {
      return true
    }

    return file.size > size ? t('common.file-size-too-big') : true
  }

export const EditProfileForm: React.FC<EditProfileFormProps> = (props) => {
  const { t } = useTranslation()
  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomEditProfileForm(), [])
  const [requestState] = useAtom(model.requestState)
  const executeRequest = useAction(model.requestState.execute)

  const [profile] = useAtom(profileAtom)
  const [isAllowedToChangeAvatar] = useAtom(isUserHypedAtom)

  const form = useForm<EditProfileFormValues>({
    mode: 'all',
    defaultValues: {
      avatar: undefined,
      firstName: profile?.firstName,
      lastName: profile?.lastName,
    },
  })
  const requestedToDeleteAvatar = form.watch('avatar') === AVATAR_DELETED

  const handleSubmit = form.handleSubmit(async () => {
    const values = form.getValues()
    const result = await executeRequest({
      // eslint-disable-next-line unicorn/no-null
      avatar: values.avatar === AVATAR_DELETED ? null : values.avatar,
      firstName: values.firstName ?? undefined,
      lastName: values.lastName ?? undefined,
    })

    if (Result.isFailure(result)) {
      return
    }

    props.onDone?.()
  })

  return (
    <Form onSubmit={handleSubmit}>
      <Avatar>
        <Controller
          control={form.control}
          rules={{
            validate: validateFileSize(AVATAR_FILESIZE_b, t),
          }}
          // disabled={requestState.loading || !isAllowedToChangeAvatar}
          name="avatar"
          render={({ field, fieldState }) => (
            <AvatarPicker
              {...field}
              disabled={!isAllowedToChangeAvatar}
              acceptableExtensions={AVATAR_EXTENSIONS}
              errorMessage={fieldState.error?.message}
              placeholder={
                requestedToDeleteAvatar ? undefined : <UserAvatar size={100} />
              }
              onDelete={() => {
                form.setValue('avatar', AVATAR_DELETED)
              }}
              tooltipContent={
                isAllowedToChangeAvatar
                  ? undefined
                  : t('profile.edit.change-avatar.only-hyped-tooltip')
              }
            />
          )}
        />
      </Avatar>
      <Controller
        control={form.control}
        disabled={requestState.loading}
        name="firstName"
        render={({ field, fieldState }) => (
          <Input
            {...field}
            label={t('common.first_name')}
            error={fieldState.error?.message}
            size="m"
          />
        )}
      />
      <Controller
        control={form.control}
        disabled={requestState.loading}
        name="lastName"
        render={({ field, fieldState }) => (
          <Input
            {...field}
            label={t('common.last_name')}
            error={fieldState.error?.message}
            size="m"
          />
        )}
      />
      <Button
        disabled={!form.formState.isValid || requestState.loading}
        busy={requestState.loading}
        type="submit"
        variant="solid"
        color="primary"
        radius="rounded"
        css={`
          margin-top: 5px;
        `}
      >
        {t('common.save')}
      </Button>
    </Form>
  )
}
const Form = styled.form`
  display: grid;
  gap: 15px;
`

const Avatar = styled.div`
  width: 100px;
  height: 100px;
  margin: auto;
  position: relative;
`
